import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { faFilter, faUndo } from "@fortawesome/free-solid-svg-icons";
import Icon from "../components/UI/icon";
import Button from "../components/UI/button";
import { ref, getDatabase, onValue, get, update } from "firebase/database";
import UsersTable from "../components/usersTable";
import Loader from "../components/UI/loader";
import FilterPopup from "../components/UI/filterPopup";
import { toastrSuccess, toastrError } from "../components/toastr";
import { addUser as addCustomer } from "../services/addUser";
import { getAllUsers } from "../services/settings";
import { v4 } from "uuid";
import { category } from "../constants/category";
import { Capacitor } from "@capacitor/core";

const UNDO_DURATION = 30;
const FREE_DAILY_LIMIT = 10;
const isIOS = Capacitor.getPlatform() === "ios";

const TABS = [
  { key: "waiting", label: "Waiting", color: "text-yellow-700 bg-yellow-50 border-yellow-200" },
  { key: "seated",  label: "Seated",  color: "text-blue-700 bg-blue-50 border-blue-200" },
  { key: "done",    label: "Done",    color: "text-green-700 bg-green-50 border-green-200" },
];

const EMPTY_FORM = { userName: "", mobileNumber: "", count: "", category: "", estimate: "", notes: "" };

const sheetInputClass =
  "w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 outline-none text-sm text-gray-900 placeholder-gray-400 focus:border-primary";

// ── Add Customer Sheet ────────────────────────────────────────────────────────
function getTodayIso() {
  // Use local date parts — toISOString() is UTC and can show "yesterday" in IST and similar timezones
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatBookingDate(dateStr) {
  const [y, mo, d] = dateStr.split("-").map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function AddCustomerSheet({ open, onClose, email, ownerId, database }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [bookingDate, setBookingDate] = useState(getTodayIso);
  const [loading, setLoading] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const [view, setView] = useState("form"); // "form" | "success" | "upgrade"
  const [users, setUsers] = useState(null);

  useEffect(() => {
    if (open) {
      setFormData(EMPTY_FORM);
      setBookingDate(getTodayIso());
      setIsReturning(false);
      setView("form");
      getAllUsers(() => {}, setUsers, email);
    }
  }, [open, email]);

  async function handleChange(e) {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);

    if (name === "mobileNumber" && value.replace(/\D/g, "").length >= 10 && !updated.userName) {
      try {
        const snap = await get(ref(database, "customers"));
        if (snap.exists()) {
          const match = Object.values(snap.val()).find(
            (c) => c.mobileNumber === value && (c.email === email || c.ownerId === ownerId) && c.userName
          );
          if (match) {
            setFormData((prev) => ({ ...prev, userName: match.userName }));
            setIsReturning(true);
          }
        }
      } catch (_) {}
    } else if (name === "mobileNumber") {
      setIsReturning(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const snap = await get(ref(database, "customers"));
      let tokenNumber = 1;
      let dateCount = 0;

      // Token and limit counted per booking date (not always today)
      const bookingDateStr = new Date(bookingDate + "T00:00:00").toDateString();

      if (snap.exists()) {
        const all = Object.values(snap.val());
        dateCount = all.filter(
          (c) => (c.email === email || c.ownerId === ownerId) &&
                 new Date(c.timeStamp).toDateString() === bookingDateStr
        ).length;
        tokenNumber = dateCount + 1;
      }

      const userType = users?.find((u) => u.email === email)?.userType || "free";
      if (!isIOS && userType === "free" && dateCount >= FREE_DAILY_LIMIT) {
        setLoading(false);
        setView("upgrade");
        return;
      }

      // Today → use current time so "just added" sorts naturally.
      // Future date → midnight local time on that date.
      const isToday = bookingDate === getTodayIso();
      const timeStamp = isToday
        ? new Date().toISOString()
        : new Date(bookingDate + "T00:00:00").toISOString();

      const data = {
        userName: formData.userName,
        mobileNumber: formData.mobileNumber,
        count: formData.count,
        estimate: formData.estimate,
        notes: formData.notes,
        category: formData.category,
        timeStamp,
        id: v4(),
        email,
        ownerId: users?.find((u) => u.email === email)?.id || ownerId,
        status: "waiting",
        tokenNumber,
      };
      await addCustomer(data);
      setLoading(false);
      setView("success");
      setTimeout(onClose, 1500);
    } catch {
      setLoading(false);
      toastrError("Could not add customer. Please try again.");
    }
  }

  const isValid = formData.userName && formData.count;

  return (
    <>
      {/* Backdrop — z-[60] to render above BottomNav (z-50) */}
      <div
        className={`fixed inset-0 z-[60] bg-black/50 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={open ? onClose : undefined}
      />

      {/* Sheet — z-[60] so it slides over the BottomNav */}
      <div
        className={`fixed inset-x-0 bottom-0 z-[60] bg-white rounded-t-3xl transition-transform duration-300 max-h-[90vh] overflow-y-auto ${open ? "translate-y-0" : "translate-y-full"}`}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-200 rounded-full" />
        </div>

        {view === "success" ? (
          <div className="flex flex-col items-center py-10 px-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900">Added to Queue!</h3>
            <p className="text-sm text-gray-500 mt-1">{formData.userName} is now waiting</p>
          </div>
        ) : view === "upgrade" && !isIOS ? (
          <div className="px-5 pb-8 pt-2">
            <div className="flex flex-col items-center py-4">
              <div className="w-16 h-16 rounded-full bg-yellow-50 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Daily Limit Reached</h3>
              <p className="text-sm text-gray-500 text-center mb-5">
                You've added {FREE_DAILY_LIMIT} customers today. Upgrade to Premium for unlimited.
              </p>
              <div className="w-full bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-xs font-semibold text-gray-700 mb-2">Contact us to upgrade:</p>
                <a href="mailto:info@pazhanam.com" className="flex items-center gap-2 text-sm text-primary font-medium mb-2">
                  info@pazhanam.com
                </a>
                <a href="https://wa.me/918778796644" className="text-sm text-green-600 font-medium">
                  +91-8778796644 (WhatsApp)
                </a>
              </div>
              <button
                onClick={onClose}
                className="w-full py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 pb-8">
            {/* Sheet header */}
            <div className="flex items-center justify-between py-3 mb-1">
              <h2 className="text-lg font-bold text-gray-900">Add Customer</h2>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
              {/* Name */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-500">Name</label>
                  {isReturning && (
                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      ↩ Returning
                    </span>
                  )}
                </div>
                <input
                  name="userName"
                  value={formData.userName}
                  onChange={handleChange}
                  className={sheetInputClass}
                  placeholder="Customer name"
                  autoFocus={false}
                />
              </div>

              {/* Mobile */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">
                  Mobile <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  className={sheetInputClass}
                  placeholder="Mobile number"
                  type="tel"
                />
              </div>

              {/* Guest count — full width */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Guest Count</label>
                <input
                  name="count"
                  value={formData.count}
                  onChange={handleChange}
                  className={sheetInputClass}
                  placeholder="No. of guests"
                  type="number"
                  min="1"
                />
              </div>

              {/* Category */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-2 block">Category</label>
                <div className="flex items-center gap-2">
                  {category.map((cate) => {
                    const selected = formData.category === cate.value;
                    return (
                      <button
                        key={cate.value}
                        type="button"
                        onClick={() => handleChange({ target: { name: "category", value: cate.value } })}
                        className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl ${
                          selected
                            ? "bg-primary-bg border border-primary"
                            : "bg-gray-50 border border-transparent"
                        }`}
                      >
                        <Icon icon={cate.icon} className={selected ? "text-primary" : "text-gray-400"} />
                        <span className={`text-xs ${selected ? "text-primary font-medium" : "text-gray-500"}`}>
                          {cate.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Wait + Date — 50/50 row */}
              <div className="flex gap-2 items-end">
                <div className="w-1/2">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">
                    Wait <span className="text-gray-400">(min)</span>
                  </label>
                  <input
                    name="estimate"
                    value={formData.estimate}
                    onChange={handleChange}
                    className={sheetInputClass}
                    placeholder="e.g. 15"
                    type="number"
                    min="0"
                  />
                </div>

                <div className="w-1/2">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Date</label>
                  <div className="relative h-[46px]">
                    <div className={`absolute inset-0 flex items-center gap-1.5 px-3 rounded-xl border pointer-events-none select-none ${
                      bookingDate !== getTodayIso()
                        ? "bg-primary/5 border-primary"
                        : "bg-gray-50 border-gray-200"
                    }`}>
                      <svg
                        className={`w-3.5 h-3.5 flex-shrink-0 ${bookingDate !== getTodayIso() ? "text-primary" : "text-gray-400"}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className={`text-xs font-semibold whitespace-nowrap ${
                        bookingDate !== getTodayIso() ? "text-primary" : "text-gray-600"
                      }`}>
                        {bookingDate === getTodayIso() ? "Today" : formatBookingDate(bookingDate)}
                      </span>
                    </div>
                    <input
                      type="date"
                      value={bookingDate}
                      min={getTodayIso()}
                      onChange={(e) => setBookingDate(e.target.value)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">
                  Notes <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className={sheetInputClass}
                  placeholder="Any special notes"
                />
              </div>

              <button
                type="submit"
                disabled={!isValid || loading}
                className={`w-full py-3.5 rounded-xl font-semibold text-sm mt-2 ${
                  isValid && !loading
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                {loading ? "Adding…" : "Add to Queue"}
              </button>
            </form>
          </div>
        )}
      </div>
    </>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard() {
  const [customers, setCustomers] = useState([]);
  const database = getDatabase();
  const customerRef = ref(database, "customers");
  const [loader, setLoader] = useState(false);
  const email = localStorage.getItem("email");
  const ownerId = localStorage.getItem("ownerId");
  const [activeTab, setActiveTab] = useState("waiting");
  const [showFilter, setFilter] = useState(false);
  const [date, setDate] = useState(new Date());
  const [type, setType] = useState("all");
  const [undoData, setUndoData] = useState(null);
  const [undoLabel, setUndoLabel] = useState("");
  const [undoSeconds, setUndoSeconds] = useState(0);
  const undoTimerRef = useRef(null);
  const undoCountdownRef = useRef(null);
  const [showAddSheet, setShowAddSheet] = useState(false);

  const clearUndoTimers = useCallback(() => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    if (undoCountdownRef.current) clearInterval(undoCountdownRef.current);
  }, []);

  useEffect(() => {
    return () => clearUndoTimers();
  }, [clearUndoTimers]);

  const handleStartUndo = useCallback(
    (customer, label) => {
      clearUndoTimers();
      setUndoData(customer);
      setUndoLabel(label);
      setUndoSeconds(UNDO_DURATION);

      undoCountdownRef.current = setInterval(() => {
        setUndoSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(undoCountdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      undoTimerRef.current = setTimeout(() => {
        setUndoData(null);
        setUndoSeconds(0);
      }, UNDO_DURATION * 1000);
    },
    [clearUndoTimers]
  );

  const handleUndo = useCallback(() => {
    if (!undoData) return;
    clearUndoTimers();

    setLoader(true);
    const custRef = ref(database, "customers");
    get(custRef).then((snapshot) => {
      if (snapshot.exists()) {
        const allData = snapshot.val();
        const userKey = Object.keys(allData).find(
          (key) => allData[key].timeStamp === undoData.timeStamp
        );
        if (userKey) {
          const customerRef = ref(database, `customers/${userKey}`);
          update(customerRef, { ...undoData, status: "waiting", tableNumber: null, tableId: null })
            .then(async () => {
              if (undoData.tableId && ownerId) {
                await update(ref(database, `tables/${ownerId}/${undoData.tableId}`), {
                  status: "free",
                  currentCustomerId: null,
                }).catch(() => {});
              }
              setLoader(false);
              toastrSuccess(`${undoData.userName} moved back to queue`);
            })
            .catch(() => {
              setLoader(false);
              toastrError("Error undoing action");
            });
        } else {
          setLoader(false);
        }
      }
    });

    setUndoData(null);
    setUndoSeconds(0);
  }, [undoData, clearUndoTimers, database]);

  useEffect(() => {
    setLoader(true);
    const unsubscribe = onValue(customerRef, (snapshot) => {
      setLoader(false);
      const data = snapshot.val();
      if (data) {
        const projects = Object.values(data).filter(
          (project) => project.email === email || project.ownerId === ownerId
        );
        setCustomers(projects);
      } else {
        setCustomers([]);
      }
    });
    return () => unsubscribe();
  }, []);

  // Derived in the same render — no extra render cycle, no stale-data flash
  const filteredData = useMemo(() => {
    const dateStr = date.toDateString();
    let filtered = customers.filter((i) => {
      const matchesDate = new Date(i.timeStamp).toDateString() === dateStr;
      const matchesType = type === "all" || i.category === type;
      let matchesTab;
      if (activeTab === "waiting") matchesTab = i.status === "waiting";
      else if (activeTab === "seated") matchesTab = i.status === "seated";
      else matchesTab = i.status === "done" || i.status === "canceled";
      return matchesDate && matchesType && matchesTab;
    });
    if (activeTab === "waiting") {
      filtered = [...filtered].sort((a, b) => {
        if (a.isPriority && !b.isPriority) return -1;
        if (!a.isPriority && b.isPriority) return 1;
        return (a.tokenNumber || 0) - (b.tokenNumber || 0);
      });
    }
    return filtered;
  }, [customers, date, type, activeTab]);

  // Counts respect the selected filter date so they always match the visible list
  const getTabCount = useCallback((tabKey) => {
    const dateStr = date.toDateString();
    if (tabKey === "waiting") return customers.filter((c) => c.status === "waiting" && new Date(c.timeStamp).toDateString() === dateStr).length;
    if (tabKey === "seated")  return customers.filter((c) => c.status === "seated"  && new Date(c.timeStamp).toDateString() === dateStr).length;
    return customers.filter((c) => (c.status === "done" || c.status === "canceled") && new Date(c.timeStamp).toDateString() === dateStr).length;
  }, [customers, date]);

  const onClose = () => setFilter(false);

  const todayStr = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  const undoRingColor = undoLabel === "seated" ? "#3b82f6" : undoLabel === "done" ? "#22c55e" : "#ef4444";

  return (
    <div className="max-w-md mx-auto">
      <div className="pl-5 pr-14 pt-6 pb-2 flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{todayStr}</p>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Dashboard</h1>
        </div>
        <Button
          onClick={() => setFilter(!showFilter)}
          className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center text-white mt-1"
        >
          <Icon icon={faFilter} className="text-sm" />
        </Button>
      </div>

      {/* Summary cards */}
      <div className="flex items-center gap-2 mx-5 mt-3 mb-1">
        {[
          { label: "Waiting",  key: "waiting",  cls: "bg-yellow-50 text-yellow-700" },
          { label: "Seated",   key: "seated",   cls: "bg-blue-50 text-blue-700" },
          { label: "Done",     key: "done",     cls: "bg-green-50 text-green-700" },
        ].map(({ label, key, cls }) => (
          <div key={key} className={`flex-1 rounded-xl p-2.5 text-center ${cls}`}>
            <p className="text-lg font-bold">{getTabCount(key)}</p>
            <p className="text-xs">{label}</p>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mx-5 mt-3 mb-3 bg-gray-100 rounded-xl p-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === tab.key
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
            {getTabCount(tab.key) > 0 && (
              <span className={`ml-1.5 text-xs font-bold px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key ? "bg-primary text-white" : "bg-gray-200 text-gray-600"
              }`}>
                {getTabCount(tab.key)}
              </span>
            )}
          </button>
        ))}
      </div>

      {filteredData && filteredData.length > 0 ? (
        <UsersTable
          customers={filteredData}
          setLoader={setLoader}
          activeTab={activeTab}
          onStartUndo={handleStartUndo}
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-16 px-6">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <h2 className="text-base font-semibold text-gray-900 mb-1">
            {activeTab === "waiting" ? "No one waiting" : activeTab === "seated" ? "No one seated" : "No completed entries"}
          </h2>
          <p className="text-sm text-gray-500 text-center">
            {activeTab === "waiting" && date.toDateString() === new Date().toDateString()
              ? "Tap the + button to add your first customer"
              : date.toDateString() !== new Date().toDateString()
              ? `No entries for ${date.toLocaleDateString()}`
              : ""}
          </p>
        </div>
      )}

      {/* Undo toast */}
      {undoData && (
        <div className="fixed bottom-20 left-4 right-4 z-50 flex justify-center">
          <div className="bg-gray-900 rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3 max-w-sm w-full">
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">
                {undoData.userName} — {undoLabel}
              </p>
              <p className="text-gray-400 text-xs">{undoSeconds}s to undo</p>
            </div>
            <div className="w-8 h-8 relative flex items-center justify-center">
              <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
                <circle cx="16" cy="16" r="14" fill="none" stroke="#374151" strokeWidth="2" />
                <circle
                  cx="16" cy="16" r="14" fill="none"
                  stroke={undoRingColor}
                  strokeWidth="2"
                  strokeDasharray={2 * Math.PI * 14}
                  strokeDashoffset={2 * Math.PI * 14 * (1 - undoSeconds / UNDO_DURATION)}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 1s linear" }}
                />
              </svg>
              <span className="absolute text-white text-xs font-bold">{undoSeconds}</span>
            </div>
            <button
              onClick={handleUndo}
              className="flex items-center gap-1.5 bg-white text-gray-900 text-sm font-semibold px-4 py-2 rounded-xl hover:bg-gray-100"
            >
              <Icon icon={faUndo} className="text-xs" />
              Undo
            </button>
          </div>
        </div>
      )}

      {/*
        FAB — hidden while undo toast is showing to avoid visual overlap.
        z-40 keeps it below the sheet (z-50) when open, above page content.
        bottom-[88px] sits 24px above the 64px BottomNav.
      */}
      {!undoData && (
        <button
          onClick={() => setShowAddSheet(true)}
          className="fixed bottom-[88px] right-4 z-40 w-14 h-14 bg-primary text-white rounded-full shadow-xl flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Add customer"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
          </svg>
        </button>
      )}

      <AddCustomerSheet
        open={showAddSheet}
        onClose={() => setShowAddSheet(false)}
        email={email}
        ownerId={ownerId}
        database={database}
      />

      {loader && <Loader loading={loader} />}
      {showFilter && (
        <FilterPopup
          open={showFilter}
          onClose={onClose}
          setDate={setDate}
          date={date}
          type={type}
          setType={setType}
        />
      )}
    </div>
  );
}

export default Dashboard;
